import { useCallback, useMemo, useState } from 'react';
import { App, Alert, Button, Popover, Space, Tag, Typography } from 'antd';

import GroupHeader from '../common/GroupHeader';
import RecordBadge from '../common/RecordBadge';
import { bulkWriteItems, rowErrorsOf } from '../../services/itemsBulk';
import { formatValue } from './itemValueDisplay';
import { getItemDisplayValue } from '../../utils/items';
import { createLookupResolver, omitUnresolvedLookups } from '../../utils/lookups';
import { normalizeId } from '../../utils/ids';
import { CHECK, CLOSE } from '../icons';
import { GRANT_READ_ONLY_REASON, isSeeOnlyElement } from '../../utils/elementAccess';
import { choiceListOf } from '../../utils/choiceValues';

/**
 * A list of items to WORK THROUGH rather than read: a medication pass, an
 * inspection round, a cycle count, an attendance roll, a prep checklist.
 *
 * The card says what that means (`options.worklist`): the element one tap
 * sets, its primary value, the alternates behind it, what an alternate
 * additionally requires ("Refused" needs a reason), how rows group, and the
 * values stamped on everything the pass writes.
 *
 * Three decisions worth naming, because they are what make this usable while
 * standing up with one hand:
 *
 * - NOTHING SAVES UNTIL THE FOOTER. Tapping a row marks it; the footer
 *   submits every mark in one request. A pass interrupted halfway has
 *   written nothing, which is the honest state — and the API refuses the
 *   whole submit if any row is invalid, so a half-saved pass is impossible
 *   rather than merely unlikely.
 * - THE EXCEPTION PATH IS THE ONE DESIGNED. The primary value is one tap;
 *   an alternate opens a small panel that asks for exactly what the card
 *   requires, because "why wasn't it given" is the entry that matters later.
 * - STAMPS ARE ASKED ONCE. `{{now}}` / `{{today}}` resolve at SUBMIT time, so
 *   a pass left open for ten minutes records when it was filed, not when it
 *   was opened.
 */

const { Text } = Typography;

/** The stamp tokens this surface resolves. Anything else is written as typed
 * — the API's own validation has the last word on it. */
const resolveStamp = (stamp, { user }) => {
	const now = new Date();
	const out = {};
	Object.entries(stamp || {}).forEach(([name, raw]) => {
		const value = `${raw ?? ''}`;
		if (value === '{{now}}') out[name] = now.toISOString();
		else if (value === '{{today}}') out[name] = now.toISOString().slice(0, 10);
		else if (value === '{{user.name}}') out[name] = user?.fullName || user?.username || '';
		else if (value === '{{user.email}}') out[name] = user?.email || '';
		else out[name] = value;
	});
	return out;
};

const labelOf = el => el?.label?.singular || el?.name || '';

/** The values an action element offers, from its own choices when it has them. */
const choiceValues = (element) => {
	// A Choice's list only (KPE-033): another type's list is inert.
	const choices = choiceListOf(element);
	return choices.map(choice => (typeof choice === 'string' ? choice : choice?.value)).filter(Boolean);
};

export default function ItemWorklist({
	items = [],
	worklist,
	card,
	elements = [],
	collectionId,
	user,
	cardsById,
	getElementsForCard,
	itemsById,
	onSaved,
}) {

	const { message } = App.useApp();
	const [ marks, setMarks ] = useState({});
	const [ saving, setSaving ] = useState(false);
	const [ rowErrors, setRowErrors ] = useState({});

	const byName = useMemo(
		() => new Map((elements || []).filter(el => el?.name).map(el => [ el.name, el ])),
		[ elements ]
	);

	const resolveElements = useMemo(() => {
		if (typeof getElementsForCard !== 'function') return () => elements || [];
		return cardId => getElementsForCard(cardId) || [];
	}, [ getElementsForCard, elements ]);

	// The same pooled resolver the grid and the table build, memoized on the
	// same three inputs: a resolver rebuilt every render throws away its label
	// cache and re-walks every nested lookup on every paint.
	const resolveLookupValue = useMemo(() => createLookupResolver({
		itemsById,
		cardsById,
		getElementsForCard: resolveElements,
	}), [ itemsById, cardsById, resolveElements ]);

	// The same resolver under the platform's omit rule, for the one caller that
	// composes a TITLE rather than a value (getItemDisplayValue takes a raw
	// resolver; formatValue applies the rule itself).
	const resolveTitleLookup = useMemo(
		() => omitUnresolvedLookups(resolveLookupValue),
		[ resolveLookupValue ]
	);

	const action = worklist?.action || {};
	const actionElement = byName.get(action.element);
	const requireName = action?.onAlternate?.require;
	const requireElement = requireName ? byName.get(requireName) : null;
	const alternates = useMemo(() => {
		if (Array.isArray(action.alternates) && action.alternates.length) return action.alternates;
		// Unstated: every other value the element offers, which is what the
		// author meant by "the alternates" often enough to be a good default.
		return choiceValues(actionElement).filter(value => value !== action.primary);
	}, [ action.alternates, action.primary, actionElement ]);

	const groups = useMemo(() => {
		if (!worklist?.groupBy) return [ { key: '', label: null, items } ];
		const groupEl = byName.get(worklist.groupBy);
		const buckets = new Map();
		items.forEach(item => {
			const raw = item?.elements?.[ worklist.groupBy ];
			// A lookup groups by the record it points at, so the bucket is
			// keyed by the normalized id. The key is IDENTITY ONLY — two rows
			// pointing at the same site belong together whatever the header
			// ends up calling it.
			const value = Array.isArray(raw) ? raw[ 0 ] : raw;
			const key = `${ normalizeId(value) || value || '' }`;
			if (!buckets.has(key)) buckets.set(key, { value, rows: [] });
			buckets.get(key).rows.push(item);
		});
		// And the LABEL is a rendering: the shared formatter turns a choice into
		// its own word and a lookup into the referenced record's title. (The
		// header used to read `rows[0].lookupTitles[groupBy]` — a field that
		// only ever exists on the share-link envelope — so on an authenticated
		// list it always fell through to printing the raw ObjectId key.)
		return [ ...buckets.entries() ].map(([ key, bucket ]) => {
			const name = labelOf(groupEl);
			if (!key) return { key, label: `No ${ name.toLowerCase() }`, items: bucket.rows };
			const text = `${ formatValue(groupEl, bucket.value, { resolveLookupValue }) ?? '' }`;
			// A reference the pool has not delivered yet renders to nothing.
			// The header names the element and waits, rather than printing the
			// id the header is keyed by.
			return { key, label: text ? `${ name }: ${ text }` : name, items: bucket.rows };
		});
	}, [ items, worklist?.groupBy, byName, resolveLookupValue ]);

	const markedCount = Object.keys(marks).length;

	const mark = useCallback((itemId, value, extra) => {
		setMarks(prev => ({ ...prev, [ itemId ]: { value, extra: extra || {} } }));
	}, []);

	const unmark = useCallback((itemId) => {
		setMarks(prev => {
			const next = { ...prev };
			delete next[ itemId ];
			return next;
		});
	}, []);

	const submit = async () => {
		const stamp = resolveStamp(worklist?.stamp, { user });
		const entries = Object.entries(marks);
		const rows = entries.map(([ _id, entry ]) => ({
			_id,
			elements: {
				...stamp,
				[ action.element ]: entry.value,
				...entry.extra,
			},
		}));
		setSaving(true);
		setRowErrors({});
		try {
			const result = await bulkWriteItems(collectionId, rows);
			const written = Array.isArray(result?.written) ? result.written.length : 0;
			if (result?.partial) {
				message.warning(`Saved ${ written } of ${ rows.length }. The rest were not written — try again.`);
			}
			else {
				message.success(written === 1 ? '1 record saved.' : `${ written } records saved.`);
			}
			setMarks({});
			onSaved?.(result);
		}
		catch (err) {
			// Nothing was written: the API validates the whole submit first.
			// The messages land on their rows, addressed by the row index.
			const byIndex = rowErrorsOf(err);
			const mapped = {};
			Object.entries(byIndex).forEach(([ index, errs ]) => {
				const id = entries[ Number(index) ]?.[ 0 ];
				if (id) mapped[ id ] = errs;
			});
			setRowErrors(mapped);
			message.error(err?.response?.data?.message || 'Nothing was saved — check the rows below.');
		}
		finally {
			setSaving(false);
		}
	};

	if (!worklist || !actionElement) return null;

	// A pass writes the action element, the alternate's required element and
	// every stamp on each row. When an element rule holds this person to "see
	// only" on any of them (KPR-509), the API refuses the whole submit
	// (`element_read_only`, reason `grant`), so the rows are shown without the
	// buttons that would build one — not offered and then refused.
	//
	// A name the pass writes that is not among this person's elements at all
	// holds it too: an element hidden from them is left out of what they are
	// served, and the API refuses a value under it like an unknown element.
	// It is never named — the person may not know it exists.
	const writtenNames = [ action.element, requireName, ...Object.keys(worklist?.stamp || {}) ].filter(Boolean);
	const heldElement = writtenNames.some(name => !byName.has(name))
		? { unnamed: true }
		: (writtenNames.map(name => byName.get(name)).find(isSeeOnlyElement) || null);

	const renderRow = (item) => {
		const id = normalizeId(item?._id);
		const entry = marks[ id ];
		const errors = rowErrors[ id ];
		const title = getItemDisplayValue({
			card: cardsById?.[ normalizeId(item?.card_id) ] || card,
			elementDefs: resolveElements(normalizeId(item?.card_id)),
			item,
			// A reference the pool has not delivered is OMITTED, so a title
			// whose template nests a lookup reads "[Site] — post-trip" until
			// the batch lands and "Salem Yard — post-trip" after — never the
			// raw id it used to print.
			//
			// NOT the stored `displayValue`: the pool misses for two reasons
			// that look identical here, and one of them is that the reference
			// is not readable by THIS viewer. `displayValue` is denormalized
			// server-side from an unscoped item query (keepr-api
			// utils/displayTitle.js), so it carries the referenced record's
			// title whether or not the reader is entitled to it. Rendering it
			// on a miss would disclose the title of a record this viewer cannot read on exactly the
			// surface the read-scoped pool exists to protect.
			resolveLookupValue: resolveTitleLookup,
		});
		return (
			<li key={id} className={`worklist__row${ entry ? ' worklist__row--marked' : '' }`}>
				<div className='worklist__row-main'>
					<RecordBadge
						kind='item'
						record={cardsById?.[ normalizeId(item?.card_id) ] || card}
						title={title}
						size='sm'
					/>
					{entry ? (
						<Space size={4} wrap>
							<Tag color={entry.value === action.primary ? 'green' : 'gold'}>{entry.value}</Tag>
							{Object.entries(entry.extra || {}).map(([ name, value ]) => (
								<Text key={name} type='secondary'>{`${ labelOf(byName.get(name)) }: ${ value }`}</Text>
							))}
						</Space>
					) : null}
					{errors ? (
						<Text type='danger' className='d-block'>{errors.map(e => e.message).join('; ')}</Text>
					) : null}
				</div>

				<div className='worklist__row-actions'>
					{heldElement ? null : entry ? (
						<Button
							size='small'
							icon={<CLOSE />}
							aria-label={`Clear ${ title }`}
							onClick={() => unmark(id)}
						/>
					) : (<>
						<Button
							type='primary'
							size='small'
							icon={<CHECK />}
							onClick={() => mark(id, action.primary)}
						>
							{action.primary}
						</Button>
						{alternates.length ? (
							<AlternatePicker
								alternates={alternates}
								requireElement={requireElement}
								onPick={(value, extra) => mark(id, value, extra)}
								itemTitle={title}
							/>
						) : null}
					</>)}
				</div>
			</li>
		);
	};

	return (
		<div className='worklist'>
			{heldElement ? (
				<Alert
					type='info'
					showIcon
					className='worklist__held'
					message={heldElement.unnamed
						? 'This list sets an element you can’t change, so you can read it but not work through it.'
						: `${ GRANT_READ_ONLY_REASON } This list sets ${ labelOf(heldElement) }, so you can read it but not work through it.`}
				/>
			) : null}
			{groups.map(group => (
				<section key={group.key} className='worklist__group'>
					{group.label ? <GroupHeader label={group.label} count={group.items.length} sticky /> : null}
					<ul className='worklist__rows'>{group.items.map(renderRow)}</ul>
				</section>
			))}

			{items.length ? null : (
				<Alert
					type='success'
					showIcon
					message='Nothing outstanding'
					description='Every record this list watches for has been handled.'
				/>
			)}

			{markedCount ? (
				<div className='worklist__footer'>
					<Text strong>{markedCount === 1 ? '1 marked' : `${ markedCount } marked`}</Text>
					<Space>
						<Button onClick={() => setMarks({})} disabled={saving}>Clear</Button>
						<Button type='primary' loading={saving} onClick={submit}>Save</Button>
					</Space>
				</div>
			) : null}
		</div>
	);
}

/** The exception path: an alternate, plus whatever the card requires with it. */
function AlternatePicker({ alternates, requireElement, onPick, itemTitle }) {
	const [ open, setOpen ] = useState(false);
	const [ value, setValue ] = useState(null);
	const [ extra, setExtra ] = useState('');

	const close = () => { setOpen(false); setValue(null); setExtra(''); };

	const content = value && requireElement ? (
		<div className='worklist__alternate'>
			<label htmlFor='worklist-required' className='d-block'>{labelOf(requireElement)}</label>
			<input
				id='worklist-required'
				className='ant-input'
				value={extra}
				autoFocus
				onChange={event => setExtra(event.target.value)}
			/>
			<Space style={{ marginTop: 8 }}>
				<Button size='small' onClick={close}>Cancel</Button>
				<Button
					size='small'
					type='primary'
					disabled={!extra.trim()}
					onClick={() => { onPick(value, { [ requireElement.name ]: extra.trim() }); close(); }}
				>
					Save
				</Button>
			</Space>
		</div>
	) : (
		<Space direction='vertical' size={4}>
			{alternates.map(alternate => (
				<Button
					key={alternate}
					size='small'
					block
					onClick={() => {
						if (requireElement) setValue(alternate);
						else { onPick(alternate); close(); }
					}}
				>
					{alternate}
				</Button>
			))}
		</Space>
	);

	return (
		<Popover
			open={open}
			onOpenChange={next => (next ? setOpen(true) : close())}
			trigger='click'
			placement='bottomRight'
			title={value ? `${ value } — why?` : null}
			content={content}
		>
			<Button size='small' aria-label={`Other outcomes for ${ itemTitle }`}>…</Button>
		</Popover>
	);
}
